import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, ConfigProvider, Modal, Result, Spin, Typography } from "antd";
import { LogoutOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { reviTheme } from "../../../../theme/reviTheme";
import { useAuth } from "../../../../hooks";
import { usePatientPortalController } from "./usePatientPortalController";
import "./Page_Patient_Portal.scss";

const { Paragraph, Title, Text } = Typography;

const YOUTUBE_ID_REGEX =
  /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|shorts\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;

function getYoutubeVideoId(url) {
  if (!url) return null;
  const raw = String(url).trim();
  const match = raw.match(YOUTUBE_ID_REGEX);
  return match?.[1] ?? null;
}

let youTubeApiPromise;

function loadYouTubeIframeAPI() {
  if (youTubeApiPromise) return youTubeApiPromise;

  youTubeApiPromise = new Promise((resolve, reject) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }

    const existing = document.getElementById("youtube-iframe-api");
    if (!existing) {
      const script = document.createElement("script");
      script.id = "youtube-iframe-api";
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = () =>
        reject(new Error("No se pudo cargar YouTube IFrame API"));
      document.head.appendChild(script);
    }

    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      if (window.YT && window.YT.Player) resolve(window.YT);
      else reject(new Error("YouTube IFrame API no inicializó correctamente"));
    };

    // Fallback por si el callback no dispara (raro en dev)
    const start = Date.now();
    const timer = window.setInterval(() => {
      if (window.YT && window.YT.Player) {
        window.clearInterval(timer);
        resolve(window.YT);
        return;
      }
      if (Date.now() - start > 10000) {
        window.clearInterval(timer);
        reject(new Error("Timeout cargando YouTube IFrame API"));
      }
    }, 100);
  });

  return youTubeApiPromise;
}

export function Page_Patient_Portal() {
  const { t } = useTranslation("auth");
  const { logout } = useAuth();
  const navigate = useNavigate();

  const { loading, error, roomUrl, iframeKey, actions } =
    usePatientPortalController();

  const showWaiting = !roomUrl;

  const handleLogout = useCallback(() => {
    logout();
    navigate("/");
  }, [logout, navigate]);

  const youtubeVideoId = useMemo(() => getYoutubeVideoId(roomUrl), [roomUrl]);
  const isYoutube = Boolean(youtubeVideoId);

  const ytRootRef = useRef(null);
  const ytPlayerRef = useRef(null);

  const [ytReady, setYtReady] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  // En el estado se guarda la CLAVE del error, no su texto. Traducir al pintar
  // evita que `t` entre como dependencia del reproductor: si entrara, cambiar de
  // idioma destruiria y recrearia el player, y el video del paciente volveria a
  // empezar desde cero en mitad de la sesion.
  const [ytErrorKey, setYtErrorKey] = useState("");

  // Evita disparar múltiples veces el auto-enable en el mismo render/ciclo
  const soundAutoTriggeredRef = useRef(false);
  const enablingSoundRef = useRef(false);

  const destroyYouTubePlayer = useCallback(() => {
    setYtReady(false);

    const player = ytPlayerRef.current;
    if (player) {
      try {
        player.destroy();
      } catch (_) {
        // ignore
      }
      ytPlayerRef.current = null;
    }

    const root = ytRootRef.current;
    if (root) root.innerHTML = "";
  }, []);

  const applyIframeAllow = useCallback((player) => {
    try {
      const iframe = player?.getIframe?.();
      if (!iframe) return;

      // Permisos útiles para reproducción embebida
      iframe.setAttribute("allow", "autoplay; encrypted-media; fullscreen");
      iframe.setAttribute("allowfullscreen", "true");
      iframe.setAttribute("referrerpolicy", "origin-when-cross-origin");
    } catch (_) {
      // ignore
    }
  }, []);

  const createYouTubePlayer = useCallback(
    async ({ videoId, startSeconds, startWithSound }) => {
      await loadYouTubeIframeAPI();

      destroyYouTubePlayer();
      setYtErrorKey("");
      setYtReady(false);

      const root = ytRootRef.current;
      if (!root) return;

      // mount fuera de React para evitar conflictos DOM
      const mount = document.createElement("div");
      mount.className = "revi-patient-portal__ytMount";
      root.appendChild(mount);

      ytPlayerRef.current = new window.YT.Player(mount, {
        videoId,
        width: "100%",
        height: "100%",
        playerVars: {
          autoplay: 1,
          controls: 1,
          playsinline: 1,
          rel: 0,
          fs: 1,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (event) => {
            const p = event.target;

            applyIframeAllow(p);

            // Reanuda desde donde estabas (si aplica)
            if (typeof startSeconds === "number" && startSeconds > 0) {
              try {
                p.seekTo(startSeconds, true);
              } catch (_) {
                // ignore
              }
            }

            try {
              p.setVolume(100);

              // Autoplay estable -> mute
              // Sonido solo cuando lo activamos tras un gesto
              if (startWithSound) {
                p.unMute();
              } else {
                p.mute();
              }

              p.playVideo();
            } catch (_) {
              // ignore
            }

            setYtReady(true);

            // Si lo creamos “con sonido”, intentamos reflejar el estado
            if (startWithSound) {
              try {
                setSoundEnabled(!p.isMuted());
              } catch (_) {
                setSoundEnabled(true);
              }
            }
          },
          onError: () => {
            setYtErrorKey("patientPortal.errors.youtubeEmbed");
          },
        },
      });
    },
    [applyIframeAllow, destroyYouTubePlayer],
  );

  // Inicializa YouTube en autoplay + mute cuando llega sesión
  useEffect(() => {
    let cancelled = false;

    soundAutoTriggeredRef.current = false;
    enablingSoundRef.current = false;

    if (!isYoutube) {
      destroyYouTubePlayer();
      setSoundEnabled(false);
      setYtErrorKey("");
      return undefined;
    }

    if (!youtubeVideoId) return undefined;

    (async () => {
      try {
        setSoundEnabled(false);
        setYtErrorKey("");

        await createYouTubePlayer({
          videoId: youtubeVideoId,
          startSeconds: 0,
          startWithSound: false, // autoplay fiable => mute
        });

        if (cancelled) return;
      } catch (_) {
        if (cancelled) return;
        setYtErrorKey("patientPortal.errors.youtubeApi");
      }
    })();

    return () => {
      cancelled = true;
      destroyYouTubePlayer();
    };
  }, [
    createYouTubePlayer,
    destroyYouTubePlayer,
    isYoutube,
    youtubeVideoId,
    iframeKey,
  ]);

  // “Pro enable”: recrea el player con sonido como resultado directo del gesto del usuario
  const enableSoundNow = useCallback(async () => {
    const current = ytPlayerRef.current;

    if (!current || !youtubeVideoId) return;
    if (enablingSoundRef.current) return;

    enablingSoundRef.current = true;

    let t = 0;
    try {
      t = Number(current.getCurrentTime?.() || 0);
    } catch (_) {
      t = 0;
    }

    try {
      setSoundEnabled(false);

      await createYouTubePlayer({
        videoId: youtubeVideoId,
        startSeconds: t,
        startWithSound: true,
      });

      // Si por alguna política siguiera muteado, el overlay seguirá visible
      setSoundEnabled(true);
    } catch (_) {
      setSoundEnabled(false);
    } finally {
      enablingSoundRef.current = false;
    }
  }, [createYouTubePlayer, youtubeVideoId]);

  // ✅ Auto-enable: en el primer gesto del usuario (PC/tablet/Quest) intentamos activar sonido
  useEffect(() => {
    if (!isYoutube) return undefined;
    if (!ytReady) return undefined;
    if (soundEnabled) return undefined;

    const onFirstGesture = () => {
      if (soundAutoTriggeredRef.current) return;
      soundAutoTriggeredRef.current = true;
      enableSoundNow();
    };

    // Capturamos “primer gesto” en cualquier parte: VR-friendly
    window.addEventListener("pointerdown", onFirstGesture, { once: true });
    window.addEventListener("touchstart", onFirstGesture, { once: true });
    window.addEventListener("keydown", onFirstGesture, { once: true });

    return () => {
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
  }, [enableSoundNow, isYoutube, soundEnabled, ytReady]);

  // Fallback a iframe si API falla o si no es YouTube
  const fallbackIframeSrc = useMemo(() => {
    if (!roomUrl) return roomUrl;

    const raw = String(roomUrl).trim();

    if (raw.includes("youtube.com/embed/")) {
      try {
        const u = new URL(raw);
        u.searchParams.set("autoplay", "1");
        u.searchParams.set("mute", "1");
        u.searchParams.set("playsinline", "1");
        u.searchParams.set("rel", "0");
        u.searchParams.set("enablejsapi", "1");
        u.searchParams.set("origin", window.location.origin);
        return u.toString();
      } catch (_) {
        return raw;
      }
    }

    return raw;
  }, [roomUrl]);

  const showYoutubeApiMode = isYoutube && !ytErrorKey;

  // Overlay VR-friendly: aparece SOLO cuando el vídeo está listo pero aún no hay audio
  const showSoundGate = showYoutubeApiMode && ytReady && !soundEnabled;

  return (
    <ConfigProvider theme={reviTheme}>
      <div className="revi-patient-portal">
        {roomUrl ? (
          showYoutubeApiMode ? (
            <div className="revi-patient-portal__youtube">
              <div
                ref={ytRootRef}
                className="revi-patient-portal__youtubeRoot"
              />

              {/* Overlay: “toca en cualquier parte” (PC/tablet/Quest) */}
              {showSoundGate ? (
                <button
                  type="button"
                  className="revi-patient-portal__soundGate"
                  onClick={enableSoundNow}
                >
                  <div className="revi-patient-portal__soundGateCard">
                    <Title level={4} style={{ marginBottom: 8 }}>
                      {t("patientPortal.soundGate.title")}
                    </Title>
                    <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                      {t("patientPortal.soundGate.hint")}
                    </Paragraph>
                    <Paragraph
                      type="secondary"
                      style={{ marginTop: 8, marginBottom: 0 }}
                    >
                      {t("patientPortal.soundGate.vrHint")}
                    </Paragraph>
                  </div>
                </button>
              ) : null}

              {/* Controles mínimos (no bloquean VR) */}
              <div className="revi-patient-portal__controls">
                <Button onClick={actions.pollNow}>
                  {t("patientPortal.controls.checkOtherSession")}
                </Button>

                {!ytReady ? (
                  <Text type="secondary">
                    {t("patientPortal.controls.loadingPlayer")}
                  </Text>
                ) : null}

                {/* Fallback manual si algún navegador sigue bloqueando */}
                {ytReady && !soundEnabled ? (
                  <Button type="primary" onClick={enableSoundNow}>
                    {t("patientPortal.controls.retrySound")}
                  </Button>
                ) : null}

                <Button danger icon={<LogoutOutlined />} onClick={handleLogout}>
                  {t("patientPortal.controls.exit")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="revi-patient-portal__iframeWrap">
              <iframe
                key={iframeKey}
                className="revi-patient-portal__iframe"
                src={fallbackIframeSrc}
                title={t("patientPortal.sessionFrameTitle")}
                allow="camera; microphone; accelerometer; gyroscope; autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; xr-spatial-tracking"
                allowFullScreen
              />
              {ytErrorKey ? (
                <div className="revi-patient-portal__ytError">
                  <Text type="warning">{t(ytErrorKey)}</Text>
                </div>
              ) : null}
            </div>
          )
        ) : (
          <div className="revi-patient-portal__backdrop" />
        )}

        <Modal
          open={showWaiting}
          centered
          closable={false}
          maskClosable={false}
          footer={null}
          destroyOnHidden
          width={520}
        >
          {loading ? (
            <div className="revi-patient-portal__waiting">
              <Spin size="large" />
              <Title level={4} style={{ marginTop: 14 }}>
                {t("patientPortal.waiting.title")}
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {t("patientPortal.waiting.subtitle")}
              </Paragraph>
              <Button
                type="text"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                className="revi-patient-portal__logout"
              >
                {t("patientPortal.waiting.endSession")}
              </Button>
            </div>
          ) : error ? (
            <Result
              status="warning"
              title={t("patientPortal.waiting.title")}
              // El subtitulo es el mensaje de error que llega del controlador,
              // no texto de interfaz: se pinta tal cual.
              subTitle={error}
              extra={[
                <Button key="retry" type="primary" onClick={actions.pollNow}>
                  {t("patientPortal.waiting.retryNow")}
                </Button>,
                <Button
                  key="logout"
                  danger
                  icon={<LogoutOutlined />}
                  onClick={handleLogout}
                >
                  {t("patientPortal.waiting.endSession")}
                </Button>,
              ]}
            />
          ) : (
            <Result
              status="info"
              title={t("patientPortal.waiting.title")}
              subTitle={t("patientPortal.waiting.subtitle")}
              extra={[
                <Button key="retry" type="primary" onClick={actions.pollNow}>
                  {t("patientPortal.waiting.checkNow")}
                </Button>,
                <Button
                  key="logout"
                  danger
                  icon={<LogoutOutlined />}
                  onClick={handleLogout}
                >
                  {t("patientPortal.waiting.endSession")}
                </Button>,
              ]}
            />
          )}
        </Modal>
      </div>
    </ConfigProvider>
  );
}
